import { zodResolver } from '@hookform/resolvers/zod';
import { ProjectKeySchema } from '@servicetim/shared';
import { useCreateProject, useCreateProjectFromTemplate, useTemplates } from '@servicetim/ui/api';
import {
  AsyncPicker,
  Button,
  Input,
  LoadingState,
  ProblemBanner,
  toast,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { TemplateCard } from '../../templates/template-card';
import { problemDetail } from '../lib/field-errors';
import { normalizeProjectKey, projectKeyProblem, suggestProjectKey } from '../lib/project-key';
import { applyProblemErrors } from '../lib/rhf-errors';
import { FormRow } from '../ui/form-row';

const BLANK = 'blank';

const NewProjectSchema = z.object({
  key: ProjectKeySchema,
  name: z.string().trim().min(1, 'A name is required').max(120),
  category: z.string().trim().max(80),
  leadId: z.string().nullable(),
});
type NewProjectValues = z.infer<typeof NewProjectSchema>;

/** Choose blank or a template, then key / name (and category / lead for blank projects). */
export function ProjectNewForm() {
  const id = useId();
  const navigate = useNavigate();
  const templates = useTemplates(100);
  const createBlank = useCreateProject();
  const createFromTemplate = useCreateProjectFromTemplate();
  const loadUsers = useUserOptionLoader();
  const [templateId, setTemplateId] = useState<string>(BLANK);
  const [keyTouched, setKeyTouched] = useState(false);

  const form = useForm<NewProjectValues>({
    resolver: zodResolver(NewProjectSchema),
    defaultValues: { key: '', name: '', category: '', leadId: null },
    mode: 'onChange',
  });
  const name = form.watch('name');
  const key = form.watch('key');

  useEffect(() => {
    if (!keyTouched) {
      form.setValue('key', suggestProjectKey(name), { shouldValidate: name.length > 0 });
    }
  }, [name, keyTouched, form]);

  const pending = createBlank.isPending || createFromTemplate.isPending;
  const error = createBlank.error ?? createFromTemplate.error;
  const keyTaken = problemDetail(error) === 'project.key_taken';
  const liveKeyProblem = key.length > 0 ? projectKeyProblem(key) : null;

  const submit = form.handleSubmit(async (values) => {
    try {
      const project =
        templateId === BLANK
          ? await createBlank.mutateAsync({
              key: values.key,
              name: values.name,
              category: values.category === '' ? null : values.category,
              leadId: values.leadId,
              settings: {
                terminology: 'request',
                portalAccessMode: 'tenant',
                portalCategories: [],
              },
            })
          : await createFromTemplate.mutateAsync({
              templateId,
              key: values.key,
              name: values.name,
            });
      toast.success(`Project ${project.key} created`);
      await navigate({ to: '/projects/$projectId/settings', params: { projectId: project.id } });
    } catch (caught) {
      applyProblemErrors(caught, form.setError, { known: ['key', 'name', 'category', 'leadId'] });
    }
  });

  return (
    <form
      className="space-y-8"
      onSubmit={(event) => {
        event.preventDefault();
        void submit(event);
      }}
    >
      <section className="space-y-3">
        <h2 className="font-semibold text-fg">1. Start from</h2>
        {templates.isPending ? (
          <LoadingState rows={2} />
        ) : (
          <div
            role="radiogroup"
            aria-label="Project template"
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            <TemplateCard
              template={{
                id: BLANK,
                name: 'Blank project',
                description:
                  'One "Task" issue type, a Default workflow (Open, In Progress, Done), three priorities and the three system roles.',
              }}
              selected={templateId === BLANK}
              onSelect={() => setTemplateId(BLANK)}
            />
            {templates.items.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                selected={templateId === t.id}
                onSelect={() => setTemplateId(t.id)}
              />
            ))}
          </div>
        )}
        {templates.error ? <ProblemBanner error={templates.error} size="sm" /> : null}
        {templates.hasMore ? (
          <Button type="button" variant="outline" size="sm" onClick={templates.loadMore}>
            More templates
          </Button>
        ) : null}
      </section>

      <section className="max-w-xl space-y-4">
        <h2 className="font-semibold text-fg">2. Name it</h2>
        {error && !keyTaken ? <ProblemBanner error={error} size="sm" /> : null}
        <FormRow
          label="Name"
          htmlFor={`${id}-name`}
          required
          error={form.formState.errors.name?.message}
        >
          <Input
            id={`${id}-name`}
            {...form.register('name')}
            maxLength={120}
            autoFocus
            aria-invalid={form.formState.errors.name ? true : undefined}
          />
        </FormRow>
        <Controller
          control={form.control}
          name="key"
          render={({ field, fieldState }) => (
            <FormRow
              label="Key"
              htmlFor={`${id}-key`}
              required
              hint="2-10 uppercase letters or digits, starting with a letter. Ticket keys look like KEY-42."
              error={
                keyTaken
                  ? 'This key (or a former key of another project) is already taken'
                  : (fieldState.error?.message ?? liveKeyProblem)
              }
            >
              <Input
                id={`${id}-key`}
                value={field.value}
                onChange={(e) => {
                  setKeyTouched(true);
                  field.onChange(normalizeProjectKey(e.target.value));
                }}
                onBlur={field.onBlur}
                maxLength={10}
                className="w-40 font-mono uppercase"
                aria-invalid={fieldState.invalid || keyTaken || liveKeyProblem ? true : undefined}
                autoComplete="off"
              />
            </FormRow>
          )}
        />
        {templateId === BLANK ? (
          <>
            <FormRow
              label="Category"
              htmlFor={`${id}-category`}
              error={form.formState.errors.category?.message}
            >
              <Input id={`${id}-category`} {...form.register('category')} maxLength={80} />
            </FormRow>
            <Controller
              control={form.control}
              name="leadId"
              render={({ field }) => (
                <FormRow
                  label="Project lead"
                  htmlFor={`${id}-lead`}
                  hint="Defaults to you when left empty."
                >
                  <AsyncPicker
                    id={`${id}-lead`}
                    value={field.value}
                    onChange={(next) => field.onChange(next ?? null)}
                    load={loadUsers}
                    placeholder="You"
                  />
                </FormRow>
              )}
            />
          </>
        ) : (
          <p className="text-fg-muted text-sm">
            Category, settings and lead come from the template; change them in the project settings
            afterwards.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={pending}>
            Create project
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/projects' })}
          >
            Cancel
          </Button>
        </div>
      </section>
    </form>
  );
}
